<template>
  <v-container fluid v-resize="onResize">
    <back-link-component :to="{ name: 'UserSettings' }" :label="$tt('customUserSettings')" />
    <v-row align="center" justify="center">
      <v-col xs="4" md="12">
        <v-card>
          <v-card-title>
            <h3>{{ $tt('customIconSettings') }}</h3>
          </v-card-title>
          <v-card-text>
            <v-row>
              <!-- DefaultIcon -->
              <v-col xs="12" sm="6" md="3">
                <v-card class="flexcard" v-bind:class="{ 'aid-selected-card': defaultSelected }">
                  <v-card-title>{{ $tt('customIcon') }}</v-card-title>
                  <v-card-text class="grow">
                    <div class="text-center mb-2">
                      <user-icon-component size="120" :showNickname="false" :iconUrl="defaultIconUrl" />
                    </div>
                    {{ $tt('customIconSettingsDescription') }}
                  </v-card-text>
                  <v-card-actions>
                    <v-btn v-if="defaultSelected" block outlined disabled>{{ $tt('customIconNowSelected') }}</v-btn>
                  </v-card-actions>
                  <v-card-actions v-if="!defaultSelected">
                    <v-btn block color="primary" class="white--text text-none" @click="changeToDefault">
                      {{ $tt('customIconChangeToIcon') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
              <!-- PresetIcon -->
              <v-col xs="12" sm="6" md="3">
                <v-card class="flexcard" v-bind:class="{ 'aid-selected-card': presetSelected }">
                  <v-card-title>{{ $tt('customPresetIcon.title') }}</v-card-title>
                  <v-card-text class="grow">
                    <div class="text-center mb-2">
                      <user-icon-component size="120" :showNickname="false" :iconUrl="selectedPresetIcon.img" />
                    </div>
                    {{ $tt('customPresetIcon.description') }}
                  </v-card-text>
                  <v-card-actions>
                    <v-btn v-if="presetSelected" block outlined disabled>{{ $tt('customIconNowSelected') }}</v-btn>
                  </v-card-actions>
                  <v-card-actions>
                    <v-btn block color="primary" class="white--text text-none" @click="isShowSelectDefaultIcon = true">
                      {{ $tt('customPresetIcon.btnText') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <!-- GravatarIcon -->
              <v-col xs="12" sm="6" md="3">
                <v-card class="flexcard" v-bind:class="{ 'selected-icon-card': gravatarSelected }">
                  <v-card-title>{{ $tt('customGravatarIcon') }}</v-card-title>
                  <v-card-text class="grow">
                    <div class="text-center mb-2">
                      <user-icon-component size="120" :showNickname="false" :iconUrl="gravatarUrl" />
                    </div>
                    {{ $tt('customIconSettingsGravatarDescription') }}
                    <a href="https://gravatar.com/" target="_blank">Gravatar</a>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn v-if="gravatarSelected" block outlined disabled>{{ $tt('customIconNowSelected') }}</v-btn>
                    <v-btn v-else block color="primary" class="white--text text-none" @click="changeToGravatar">
                      {{ $tt('customIconChangeToGravatar') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <!-- CustomIcon -->
              <v-col xs="12" sm="6" md="3">
                <v-card class="flexcard" v-bind:class="{ 'selected-icon-card': customSelected }">
                  <v-card-title>{{ $tt('customCustomIcon') }}</v-card-title>
                  <v-card-text class="grow">
                    <div class="text-center mb-2">
                      <user-icon-component size="120" :showNickname="false" :iconUrl="customIconUrl" />
                    </div>
                    {{ $tt('customIconSettingsCustomDescription') }}
                  </v-card-text>
                  <div v-if="isCustomIconUploaded">
                    <v-card-actions>
                      <v-btn block color="primary" class="white--text text-none" @click="showUploadDialog">
                        {{ $tt('customIconReUpload') }}
                      </v-btn>
                    </v-card-actions>
                    <v-card-actions v-if="!customSelected">
                      <v-btn block color="primary" class="white--text text-none" @click="changeToCustom">
                        {{ $tt('customIconChangeToCustom') }}
                      </v-btn>
                    </v-card-actions>
                  </div>
                  <div v-if="!isCustomIconUploaded">
                    <v-card-actions>
                      <v-btn block color="primary" class="white--text text-none" @click="showUploadDialog">
                        {{ $tt('customUpload') }}
                      </v-btn>
                    </v-card-actions>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <image-upload
      :class="{ 'upload-mobile-style': isMobile }"
      field="img"
      @crop-success="onImageCropSuccess"
      @crop-upload-success="onImageUploadSuccess"
      @crop-upload-fail="onImageUploadFailure"
      v-model="showImageUpload"
      :width="200"
      :height="200"
      :url="imageUploadEndPoint"
      :headers="imageUploadHeaders"
      img-format="png"
      langType="ja"
    />
    <v-dialog width="500" v-model="isShowSelectDefaultIcon">
      <v-card>
        <v-card-title>{{ $tt('customSelectDefaultIconTitle') }}</v-card-title>
        <v-card-text class="px-8">
          <v-item-group mandatory>
            <v-row>
              <v-col cols="6" md="4" @click="changeSelectPresetIcon(icon)" v-for="icon in presetIcons" :key="icon.name">
                <v-item>
                  <v-layout
                    justify-center
                    align-center
                    :class="{ activeIcon: icon.isActiveIcon }"
                    style="border-radius:75px;height: 130px;min-height: 130px; min-width: 130px; width: 130px;margin-left: auto;
    margin-right: auto;"
                  >
                    <user-icon-component size="120" :showNickname="false" :iconUrl="icon.img" />
                  </v-layout>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>
          <v-btn block color="primary" class="white--text text-none mt-2 pa-2" @click="changeToPreset">
            {{ $tt('customChange') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import UserIconComponent from '@/components/user/UserIconComponent'
import ApplicationConfig from '@/lib/application-config'
import * as Constants from '@/lib/constants'
import UserInfo from '@/lib/user-info'
import ImageUpload from 'vue-image-crop-upload'
import connectAuthClient from '@/lib/connect-client/connect-auth-client'

const gravatar = require('gravatar')

export default {
  components: { ImageUpload, UserIconComponent },

  name: 'user-icon',
  mounted: function() {
    this.getMe()
  },
  data() {
    return {
      defaultIconUrl: Constants.DEFAULT_ICON_URL,
      showImageUpload: false,
      isMobile: false,
      isShowSelectDefaultIcon: false,
      presetIcons: [
        { name: Constants.ICON_PRESET_1, img: Constants.ICON_PRESET_1_URL, isActiveIcon: true },
        { name: Constants.ICON_PRESET_2, img: Constants.ICON_PRESET_2_URL, isActiveIcon: false },
        { name: Constants.ICON_PRESET_3, img: Constants.ICON_PRESET_3_URL, isActiveIcon: false },
        { name: Constants.ICON_PRESET_4, img: Constants.ICON_PRESET_4_URL, isActiveIcon: false },
        { name: Constants.ICON_PRESET_5, img: Constants.ICON_PRESET_5_URL, isActiveIcon: false },
        { name: Constants.ICON_PRESET_6, img: Constants.ICON_PRESET_6_URL, isActiveIcon: false },
      ],
      isActiveIcon: false,
      selectedPresetIcon: { name: Constants.ICON_PRESET_1, img: Constants.ICON_PRESET_1_URL },
    }
  },
  computed: {
    gravatarUrl: function() {
      const email = UserInfo.getEmail()
      return gravatar.url(email, { d: 'mm' }, true)
    },
    customIconUrl: function() {
      return UserInfo.getCustomIconUrl()
    },
    defaultSelected: function() {
      return UserInfo.getIcon() === Constants.ICON_DEFAULT
    },
    presetSelected: function() {
      // ファイル名でのみ判定が可能
      const fileName = UserInfo.getIconUrl()
        .split('/')
        .pop()
      const preset1 = Constants.ICON_PRESET_1_URL.split('/').pop()
      const preset2 = Constants.ICON_PRESET_2_URL.split('/').pop()
      const preset3 = Constants.ICON_PRESET_3_URL.split('/').pop()
      const preset4 = Constants.ICON_PRESET_4_URL.split('/').pop()
      const preset5 = Constants.ICON_PRESET_5_URL.split('/').pop()
      const preset6 = Constants.ICON_PRESET_6_URL.split('/').pop()
      return (
        fileName === preset1 ||
        fileName === preset2 ||
        fileName === preset3 ||
        fileName === preset4 ||
        fileName === preset5 ||
        fileName === preset6
      )
    },
    gravatarSelected: function() {
      return UserInfo.getIcon() === Constants.ICON_GRAVATAR
    },
    customSelected: function() {
      // プリセットもカスタムとして扱うので、プリセットが選択されている場合はカスタムに変更ボタンをオンにする
      if (this.presetSelected) return false
      return UserInfo.getIcon() === Constants.ICON_CUSTOM
    },
    imageUploadHeaders: function() {
      const accessToken = this.$store.getters.accessToken
      return { Authorization: accessToken }
    },
    imageUploadEndPoint: function() {
      return connectAuthClient.endpoint('/v1/user/icon/custom')
    },
  },
  methods: {
    onResize() {
      if (window.innerWidth < Constants.RESPONSIVE_BREAKPOINT_USER_ICON) this.isMobile = true
      else this.isMobile = false
    },
    changeToDefault() {
      this.$store.dispatch('authUpdateUserIcon', { icon: Constants.ICON_DEFAULT })
    },
    changeToPreset() {
      // プリセットはカスタムとして扱う（NAVIアプリだとDefaultにすると固定URLしか読まないため）
      this.$store.dispatch('authUpdateUserIcon', { icon: Constants.ICON_CUSTOM, preset: this.selectedPresetIcon.name })
      this.isShowSelectDefaultIcon = false
    },
    changeToGravatar() {
      this.$store.dispatch('authUpdateUserIcon', { icon: Constants.ICON_GRAVATAR })
    },
    changeToCustom() {
      this.$store.dispatch('authUpdateUserIcon', { icon: Constants.ICON_CUSTOM })
    },
    getMe() {
      this.$store.dispatch('authGetMe')
    },
    isCustomIconUploaded() {
      if (this.customIconUrl) {
        if (this.customIconUrl.endsWith('undefined.png')) {
          return false
        }
      }
      return true
    },
    showUploadDialog() {
      this.showImageUpload = true
    },
    onImageCropSuccess() {},
    onImageUploadSuccess() {
      this.getMe()
    },
    onImageUploadFailure() {},
    changeSelectPresetIcon(icon) {
      this.presetIcons.forEach(icon => {
        icon.isActiveIcon = false
      })
      icon.isActiveIcon = true
      this.selectedPresetIcon = icon
    },
  },
}
</script>

<style scoped>
.flexcard {
  display: flex;
  flex-direction: column;
  min-height: 0px;
}

@media (min-width: 600px) {
  .flexcard {
    min-height: 370px;
  }
}
</style>
<style>
.upload-mobile-style .vicp-wrap {
  width: 330px !important;
  height: 530px !important;
}

.activeIcon {
  background-color: #ddd;
}
</style>
